if (!customElements.get('nolvya-catalogue-rug-scroll')) {
  customElements.define('nolvya-catalogue-rug-scroll', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.boot = requestAnimationFrame(() => { if (this.isConnected) this.initialize(); });
    }
    initialize() {
      this.track = this.querySelector('.nr-track');
      this.scene = this.querySelector('.nr-scene');
      this.plane = this.querySelector('.nr-plane');
      this.image = this.querySelector('.nr-rug-image');
      this.rug = this.querySelector('.nr-rug');
      this.features = [...this.querySelectorAll('.nr-feature')];
      if (!this.track || !this.scene || !this.plane) return;
      this.config = JSON.parse(this.dataset.config);
      this.imageRatio = Number(this.dataset.imageRatio) || 3.2;
      this.nodes = this.features.map(feature => ({
        feature, detail: feature.querySelector('.nr-detail'),
        path: feature.querySelector('.nr-connector-line'),
        direction: feature.classList.contains('nr-top') ? -1 : 1
      }));
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.progress = 0;
      this.target = 0;
      this.lastScroll = window.scrollY;
      this.visible = true;
      this.positionDirty = true;
      this.imagesReady = !this.image;
      this.measure = () => { this.layoutDirty = true; this.schedule(); };
      this.viewportWidth = window.innerWidth;
      this.onResize = () => {
        const width = window.innerWidth;
        if (width < 768 && width === this.viewportWidth) return;
        this.viewportWidth = width; this.measure();
      };
      this.onScroll = () => { this.positionDirty = true; this.schedule(); };
      this.onVisibility = () => {
        if (document.hidden) { cancelAnimationFrame(this.frame); this.frame = 0; }
        else { this.positionDirty = true; this.schedule(); }
      };
      window.addEventListener('scroll', this.onScroll, {passive:true});
      window.addEventListener('resize', this.onResize, {passive:true});
      document.addEventListener('visibilitychange', this.onVisibility);
      this.motion.addEventListener('change', this.measure);
      this.resizeObserver = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        if (Math.abs(width - (this.observedWidth || 0)) > .5) {
          this.observedWidth = width;
          this.measure();
        }
      });
      this.resizeObserver.observe(this.track);
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) { this.positionDirty = true; this.schedule(); }
        else {
          cancelAnimationFrame(this.frame);
          this.frame = 0;
          this.removeAttribute('data-animating');
        }
      }, {rootMargin:'200px'});
      this.intersectionObserver.observe(this.track);
      this.images = [...this.querySelectorAll('img')];
      this.onImageReady = () => {
        const decode = this.image?.decode ? this.image.decode().catch(() => {}) : Promise.resolve();
        decode.then(() => {
          if (!this.isConnected) return;
          this.imagesReady = true;
          this.positionDirty = true;
          this.schedule();
        });
      };
      this.images.forEach(img => {
        img.addEventListener('load', this.onImageReady);
        img.addEventListener('error', this.onImageReady);
      });
      if (this.image?.complete) this.onImageReady();
      this.refresh();
      this.render();
      this.schedule();
      document.fonts?.ready.then(() => { if (this.isConnected) this.measure(); });
    }
    schedule() {
      if (this.frame || !this.visible || document.hidden) return;
      this.frame = requestAnimationFrame(now => {
        this.frame = 0;
        if (!this.isConnected) return;
        if (this.layoutDirty) { this.layoutDirty = false; this.refresh(); }
        if (this.positionDirty) { this.positionDirty = false; this.checkPosition(); }
        this.tick(now);
        if (this.transition) this.schedule();
      });
    }
    refresh() {
      this.vh = document.documentElement.clientHeight || window.innerHeight || 800;
      this.staticMode = this.dataset.editor === 'true' || this.dataset.preview === 'true' || this.dataset.enabled !== 'true' || this.motion.matches;
      if (this.staticMode) { this.transition = null; this.progress = this.target = 1; }
      this.toggleAttribute('data-static', this.staticMode);
      this.toggleAttribute('data-ready', !this.staticMode);
      this.layout();
      this.style.setProperty("--nr-plane-width", this.geometry().width + "px");
      this.lastRendered = null;
      this.positionDirty = true;
    }
    viewportHeight() { return this.vh; }
    checkPosition() {
      const current = window.scrollY, delta = current - this.lastScroll;
      this.lastScroll = current;
      if (this.staticMode || !this.imagesReady) return;
      const track = this.track.getBoundingClientRect();
      const sceneTop = this.scene.getBoundingClientRect().top;
      const vh = this.viewportHeight();
      if (this.hasAttribute('data-tall')) {
        if (sceneTop <= vh * .8 && track.bottom > 0) this.playTo(1);
        return;
      }
      const rugTop = sceneTop + this.geometry().y;
      const returnAt = vh * (Number(this.dataset.return) || 72) / 100;
      if (delta < -1 && rugTop >= returnAt) { this.playTo(0); return; }
      if (sceneTop <= vh * .72 && track.bottom > 0) this.playTo(1);
    }
    playTo(target) {
      if (this.staticMode || this.target === target) return;
      this.target = target;
      const mobile = window.innerWidth < 750;
      const duration = mobile ? Math.min(550, Number(this.dataset.duration) || 550) : Number(this.dataset.duration) || 950;
      this.transition = {from:this.progress, to:target, start:performance.now(), duration:Math.max(150, duration * Math.abs(target - this.progress))};
      this.setAttribute('data-animating', '');
      this.schedule();
    }
  geometry(){return this.rugGeometry||{x:0,y:0,width:1,height:1}}
  layout(){
   const W=this.scene.clientWidth;if(W<1)return;
   if(window.innerWidth>=750&&this.dataset.compact==='true'){this.compactLayout(W);return}
   const mobile=window.innerWidth<750,e=mobile?4:24;
   this.style.setProperty('--nr-title',(mobile?this.config.mobile_title_size:this.config.title_size)+'px');
   this.style.setProperty('--nr-text',(mobile?this.config.mobile_text_size:this.config.text_size)+'px');
   const circle=Math.min(mobile?this.config.mobile_circle_size:this.config.circle_size,W*.22);
   this.style.setProperty('--nr-circle',circle+'px');
   const ratio=this.imageRatio;
   const padFactor=Math.max(1,(Number(this.dataset.padScale)||99)/100+2*Math.abs(Number(this.dataset.padOffset)||0)/100);
   const width=Math.max(1,(W-2*e)/padFactor),height=width/ratio;
   const rugX=(W-width)/2,gap=mobile?22:34;
   const boxes=this.features.map(feature=>{
    const copy=feature.querySelector('.nr-copy'),detail=feature.querySelector('.nr-detail'),slot=Number(feature.dataset.slot);
    const copyWidth=Math.min(W*.45,mobile?230:340);
    if(copy)copy.style.width=copyWidth+'px';
    return {feature,slot,copy,detail,w:copyWidth};
   });
   for(const b of boxes)b.h=b.copy?.offsetHeight||0;
   const bySlot=n=>boxes.find(b=>b.slot===n),left=bySlot(0),right=bySlot(1);
   const topGap=mobile?Math.max(130,W*.36):Math.max(180,W*.20);
   const y=Math.max((right?.h||0)+topGap+(right?.detail?circle*.55:0),(left?.h||0)+(mobile?100:160));
   const base=y+height;
   let H=base+e;
   for(const b of boxes){
    if(!b.copy)continue;
    const isRight=b.slot%2===1;
    const x=isRight?W-e-b.w:e;
    const dx=Math.max(e,Math.min(W-e-circle,rugX+width*(isRight?.73:.19)-circle/2));
    let cy,dy;
    if(b.slot===0){cy=Math.max(e,y-b.h-(mobile?58:90));dy=y-circle*.25;}
    else if(b.slot===1){cy=e;dy=y-circle*.55;}
    else if(b.slot===2){dy=base-circle*.20;cy=base+(b.detail?circle+gap:mobile?100:150);}
    else{dy=base-circle*.30;cy=base+(b.detail?circle*.70+(mobile?76:100):mobile?70:110);}
    b.x=x;b.y=cy;b.dx=dx;b.dy=dy;
    if(this.dataset.layout==='auto'){
     b.copy.style.left=x+'px';b.copy.style.top=cy+'px';b.copy.style.textAlign='center';
     if(b.detail){b.detail.style.left=dx+'px';b.detail.style.top=dy+'px';}
    }
    H=Math.max(H,cy+b.h+e,b.detail?dy+circle+e:0);
   }
   H=Math.ceil(Math.max(H,mobile?420:600));
   this.scene.style.height=H+'px';this.style.setProperty('--scene-height',H+'px');
   this.toggleAttribute('data-tall',H>this.viewportHeight()-(mobile?20:Number(getComputedStyle(this).getPropertyValue('--sticky-top').replace('px',''))||0));
   this.plane.style.left=rugX+'px';this.plane.style.top=y+'px';this.plane.style.width=width+'px';this.plane.style.height=height+'px';
   this.rugGeometry={x:rugX,y,width,height};
   if(this.dataset.layout==='auto')this.connectors(boxes,circle,W,H);
   else this.manualConnectors(W,H);
  }
  compactLayout(W) {
  this.toggleAttribute('data-no-benefits', !this.querySelector('.nr-benefits'));
  const e = 8;
  const circle = Math.min(Number(this.dataset.detailCircle) || 90, W * .16, 90);
  const ratio = this.imageRatio;
  const scale = Math.max(1, (Number(this.dataset.padScale) || 99) / 100
    + 2 * Math.abs(Number(this.dataset.padOffset) || 0) / 100);
  const sy = (Number(this.dataset.padScaleY) || 99) / 100;
  const oy = (Number(this.dataset.padOffsetY) || 0) / 100;
  const width = Math.max(1, (W - 2 * e) / scale);
  const height = width / ratio;
  const rugX = (W - width) / 2;
  this.style.setProperty('--nr-circle', circle + 'px');
  this.style.setProperty('--nr-title', '16px');
  this.style.setProperty('--nr-text', '14px');
  const boxes = this.features.map(feature => {
    const copy = feature.querySelector('.nr-copy');
    const detail = feature.querySelector('.nr-detail');
    const slot = Number(feature.dataset.slot);
    const w = Math.min(W * .43, 280);
    feature.toggleAttribute('data-has-detail', !!detail);
    if (copy) {
      copy.style.width = w + 'px';
      copy.style.textAlign = 'center';
    }
    return {feature, copy, detail, slot, w};
  });
  boxes.forEach(b => { b.h = b.copy?.offsetHeight || 0; });
  const tops = boxes.filter(b => b.slot < 2);
  const upperSpace = Math.max(0, ...tops.map(b =>
    b.h + (b.detail ? circle * .5 + 70 : 65)
  ));
  const y = e + upperSpace + Math.max(0, (sy - 1) / 2 - oy) * height;
  const base = y + height;
  const lowerEdge = Math.max(base, y + height * ((1 + sy) / 2 + oy));
  let H = lowerEdge + e;
  for (const b of boxes) {
    if (!b.copy) continue;
    const right = b.slot % 2 === 1;
    b.x = right ? W - e - b.w : e;
    b.dx = Math.max(e, Math.min(W - e - circle,
      rugX + width * (right ? .74 : .20) - circle / 2));
    if (b.slot < 2) {
      b.dy = y - circle * .55;
      b.y = b.detail ? e : Math.max(e, y - b.h - 36);
    } else {
      b.dy = lowerEdge - circle * .2;
      b.y = b.detail ? b.dy + circle + 42 : lowerEdge + 42;
    }
    Object.assign(b.copy.style, {left:b.x + 'px', top:b.y + 'px'});
    if (b.detail) Object.assign(b.detail.style, {
      left:b.dx + 'px', top:b.dy + 'px'
    });
    H = Math.max(H, b.y + b.h + e, b.detail ? b.dy + circle + e : 0);
  }
  H = Math.ceil(Math.max(H, Math.min(Number(this.dataset.desktopHeight) || 360, 480)));
  this.scene.style.height = H + 'px';
  this.style.setProperty('--scene-height', H + 'px');
  this.removeAttribute('data-tall');
  Object.assign(this.plane.style, {
    left:rugX + 'px', top:y + 'px', width:width + 'px', height:height + 'px'
  });
  this.rugGeometry = {x:rugX, y, width, height};
  this.connectors(boxes, circle, W, H);
  }
  baseConnectors(boxes,circle,W,H){
   const r=this.geometry(),unit=Math.max(.4,Math.min(1,r.width/738));
   for(const b of boxes){
    const path=b.feature.querySelector('.nr-connector-line'),svg=b.feature.querySelector('.nr-connector');if(!path||!b.copy)continue;
    svg?.setAttribute('viewBox',`0 0 ${W} ${H}`);
    let sx,sy,ex,ey;
    if(b.slot===0){sx=b.x+b.w*.16;sy=b.y+b.h+10;ex=r.x+r.width*.10;ey=r.y+r.height*.55;}
    else if(b.slot===1){sx=b.x+b.w*.76;sy=b.y+b.h+14;ex=b.detail?b.dx+circle*.65:r.x+r.width*.78;ey=b.detail?b.dy-5:r.y+r.height*.20;}
    else if(b.slot===2){sx=b.x+b.w*.70;sy=b.y-14;ex=b.detail?b.dx+circle*.5:r.x+r.width*.28;ey=b.detail?b.dy+circle+5:r.y+r.height*.88;}
    else{sx=b.x+b.w*.30;sy=b.y-14;ex=b.detail?b.dx+circle*.44:r.x+r.width*.76;ey=b.detail?b.dy+circle+5:r.y+r.height*.80;}
    this.path(path,b.feature,sx,sy,ex,ey,unit);
   }
  }
  manualConnectors(W,H){
   for(const feature of this.features){
    const p=feature.querySelector('.nr-connector-line'),svg=feature.querySelector('.nr-connector');if(!p)continue;
    const w=feature.clientWidth,h=feature.clientHeight;
    if(svg)svg.setAttribute('viewBox',`0 0 ${w} ${h}`);
    this.path(p,feature,w*.5,0,w*.35,h,1);
   }
  }
  path(p,f,sx,sy,ex,ey,scale){
   sx+=(Number(f.dataset.arrowStartX)||0)*scale;sy+=(Number(f.dataset.arrowStartY)||0)*scale;
   ex+=(Number(f.dataset.arrowEndX)||0)*scale;ey+=(Number(f.dataset.arrowEndY)||0)*scale;
   const bend=(Number(f.dataset.arrowCurve)||0)*scale,dy=ey-sy;
   p.setAttribute('d',`M ${sx} ${sy} C ${sx-bend} ${sy+dy*.5},${ex-bend*.5} ${sy+dy*.8},${ex} ${ey}`);
  }
  connectors(boxes, circle, W, H) {
    const desktop = window.innerWidth >= 750 && this.dataset.compact === 'true';
    if (!desktop) return this.baseConnectors(boxes, circle, W, H);
    const bottom = boxes.find(b => b.slot === 3 && b.detail && b.copy);
    if (bottom) {
      bottom.y = Math.max(bottom.y, bottom.dy + circle + 64);
      bottom.copy.style.top = bottom.y + 'px';
      H = Math.ceil(Math.max(H, bottom.y + bottom.h + 8));
      this.scene.style.height = H + 'px';
      this.style.setProperty('--scene-height', H + 'px');
    }
    this.baseConnectors(boxes, circle, W, H);
    for (const b of boxes) {
      if ((b.slot !== 1 && b.slot !== 3) || !b.copy || !b.detail) continue;
      const path = b.feature.querySelector('.nr-connector-line');
      if (!path) continue;
      const top = b.slot === 1;
      const sx = b.x + b.w * .5;
      const sy = top ? b.y + b.h + 12 : b.y - 12;
      const ex = b.dx + circle * .5;
      const ey = top ? b.dy - 8 : b.dy + circle + 8;
      const dy = ey - sy;
      const bend = Math.min(6, W * .01);
      path.setAttribute('d', `M ${sx} ${sy} C ${sx+(ex-sx)*.2+bend} ${sy+dy*.35}, ${sx+(ex-sx)*.8+bend} ${sy+dy*.70}, ${ex} ${ey}`);
    }
  }

    tick(now) {
      if (this.transition) {
        const t = Math.max(0, Math.min(1, (now - this.transition.start) / this.transition.duration));
        this.progress = this.transition.from + (this.transition.to - this.transition.from) * t;
        if (t === 1) this.transition = null;
      }
      this.render();
      if (!this.transition) this.removeAttribute('data-animating');
    }
    render() {
      const p = this.staticMode ? 1 : this.progress;
      if (p === this.lastRendered) return;
      this.lastRendered = p;
      const clamp = v => Math.max(0, Math.min(1,v)), smooth = v => v * v * (3 - 2 * v);
      const land = Math.max(.1, Number(this.dataset.land) / 100 || .62);
      const start = Math.max(land + .04, Math.min(.9, Number(this.dataset.reveal) / 100 || .72));
      const descent = smooth(clamp(p / land));
      const reveal = smooth(clamp((p - start) / Math.max(.01, .96 - start)));
      const r = this.geometry();
      const gap = Math.min(r.height * (Number(this.dataset.gap) || 30) / 100, Math.max(0, r.y - 12));
      this.rug.style.transform = `translate3d(0,${-gap * (1 - descent)}px,0)`;
      for (const node of this.nodes) {
        node.feature.style.transform = `translate3d(0,${14 * (1 - reveal) * node.direction}px,0)`;
        node.feature.style.opacity = String(reveal);
        node.feature.style.visibility = reveal > 0 ? 'visible' : 'hidden';
        if (node.detail) node.detail.style.transform = `scale(${.8 + .2 * reveal})`;
        if (node.path) node.path.style.strokeDashoffset = String(125 * (1 - reveal));
      }
    }
    disconnectedCallback() {
      this.initialized = false;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.images?.forEach(img => {
        img.removeEventListener('load', this.onImageReady);
        img.removeEventListener('error', this.onImageReady);
      });
      this.motion?.removeEventListener('change', this.measure);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      cancelAnimationFrame(this.boot);
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.transition = null;
    }
  });
}

